import React from 'react';
import noop from 'lodash/noop';
import { Paper, ComplexValue, ElementLoader, Search } from 'ui';
import { Breadcrumb } from 'ui/common/breadcrumb/breadcrumb';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';

import { AdminInfoData } from 'app/types-shared';
import { deepEquals } from 'app/infrastructure/js';
import { ResizeWrapper } from 'app/components/resize-wrapper';
import { buildBreadcrumbs } from 'app/area/shared/codetable/breadcrumbs';
import { CodeSelectorTranslation } from 'app/area/code-selector/constants';
import { SelectOptionValue } from 'app/area/code-selector/types';

import { getResolver } from 'app/area/shared/codetable/get-resolver';
import { ListViewColumnContainer } from './list-view-column-container';
import { getOptions, shouldDisplaySingleFlexibleColumn } from './list-view-code-selector-helpers';

import styles from './list-view-code-selector.scss';

export interface ListViewCodeSelectorProps {
  title?: string;
  options: SelectOptionValue[];
  fetching: boolean;
  routeHandler?: (route: string | object) => void;
  urlResolver: (value: number[] | number) => string | { pathname: string; search: string };
  onOpen?: () => void;
  breadcrumbs: number[];
  translation: CodeSelectorTranslation;
  adminPanelAccess: AdminInfoData;
  checkCodetableAccess: (adminPanelAccess: AdminInfoData, translation: CodeSelectorTranslation) => void;
}

export interface ListViewCodeSelectorState {
  breadcrumbs: ComplexValue<number>[];
  searchInput: string;
}

export class ListViewCodeSelector extends React.Component<ListViewCodeSelectorProps, ListViewCodeSelectorState> {

  public state: ListViewCodeSelectorState = {
    searchInput: '',
    breadcrumbs: []
  };

  public componentDidMount() {
    const { fetching, onOpen, adminPanelAccess, translation, checkCodetableAccess } = this.props;

    if (!fetching) {
      onOpen && onOpen();
    }
    checkCodetableAccess(adminPanelAccess, translation);
  }

  public componentDidUpdate(prevProps: ListViewCodeSelectorProps) {
    const { checkCodetableAccess, adminPanelAccess, translation } = this.props;
    if (this.shouldBuildBreadcrumbs(prevProps)) {
      const breadcrumbs = buildBreadcrumbs(this.props.options, this.props.breadcrumbs);
      this.setState({ breadcrumbs });
    }

    if (!deepEquals(adminPanelAccess, prevProps.adminPanelAccess) || !deepEquals(translation, prevProps.translation)) {
      checkCodetableAccess(adminPanelAccess, translation);
    }
  }

  private shouldBuildBreadcrumbs = (prevProps: ListViewCodeSelectorProps) => !deepEquals(prevProps.options, this.props.options)
    && prevProps.options && this.props.options && this.props.options.length > 0
    && this.props.breadcrumbs && this.props.breadcrumbs.length > 0;

  private selectFolder = (breadcrumbs: ComplexValue<number>[]) => this.setState({ breadcrumbs });

  private onClickBreadcrumbItem = (item: ComplexValue<number>) => {
    const breadcrumbs = [...this.state.breadcrumbs];

    if (breadcrumbs !== undefined && breadcrumbs.length > 0 && item !== undefined) {
      while (breadcrumbs[breadcrumbs.length - 1] && breadcrumbs[breadcrumbs.length - 1].value !== item.value) {
        breadcrumbs.pop();
      }
    }

    this.setState({ breadcrumbs });
  };

  private onClickBreadcrumbHome = () => {
    this.setState({ breadcrumbs: [] });
  };

  private selectItem = (breadcrumbs: number[]) => {
    const { urlResolver, routeHandler } = this.props;
    const resolver = getResolver(breadcrumbs) ?? urlResolver;

    routeHandler && routeHandler(resolver(breadcrumbs));
  };

  private onSearchInputChange = (input: string) => {
    const trimmedInput = input?.trim();

    this.setState({
      searchInput: trimmedInput,
      ...((trimmedInput !== '' && trimmedInput !== undefined) && { breadcrumbs: [] })
    });
  };

  public render() {
    const { breadcrumbs, searchInput } = this.state;
    const { fetching, title, translation } = this.props;

    const options = getOptions(this.state.searchInput, this.props.options);

    return (
      <div className="entity-view">
        <Paper componentId="code-base-paper" className={undefined}>
          {fetching
            ? <ElementLoader />
            : <MainGridContainer>
              <div className={styles.titleContainer}>
                <div id="list-view-code-selector-title" className="row">
                  <div className="col-12">
                    <h1>{title}</h1>
                  </div>
                </div>
              </div>
              <div className="row">
                <div id="list-view-codetable-search-container" className={`${styles.searchContainer} col-lg-10 col-md-12 col-xl-5`}>
                  <Search
                    className="input-wrapper"
                    componentId="list-view-codetable-search"
                    onChange={this.onSearchInputChange}
                    value={searchInput}
                    minLength={1}
                    maxLength={255}
                    onSearch={noop}
                    placeholder={translation.search}
                    focus={true} />
                </div>
              </div>
              <div className={styles.titleBreadcrumb}>
                <Breadcrumb
                  items={breadcrumbs}
                  onClickItem={this.onClickBreadcrumbItem}
                  onClickBreadcrumbHome={this.onClickBreadcrumbHome}
                  homeLabel=""
                  home={true} />
              </div>
              <ResizeWrapper>
                {
                  !!options && options.length > 0
                    ? <ListViewColumnContainer
                      breadcrumbs={breadcrumbs}
                      options={options}
                      selectFolder={this.selectFolder}
                      selectItem={this.selectItem}
                      displaySingleFlexibleColumn={shouldDisplaySingleFlexibleColumn(searchInput)}
                    />
                    : <div>{translation.no_results}</div>
                }
              </ResizeWrapper>
            </MainGridContainer>
          }
        </Paper>
      </div>
    );
  }
}
